import { useEffect, useState } from "react";
import { useUpdateContext } from "../context/UpdateContext";
import { FcGenericSortingAsc, FcGenericSortingDesc } from "react-icons/fc";
import { Prospect } from "../schemas/Prospect";
import supabase from "../services/supabaseClient";
import { useLanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils";

const ProspectsTerminalPage = () => {
  const [prospects, setProspects] = useState<Prospect[]>(() => {
    const storedData = localStorage.getItem("prospects");
    return storedData ? JSON.parse(storedData) : [];
  });
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<{ field: string; direction: string }>({
    field: "last_update",
    direction: "desc",
  });
  const { isHE } = useLanguageContext();
  const { didUpdate } = useUpdateContext();
  const navigate = useNavigate();

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase.rpc("get_prospects_list");
      if (error) {
        console.error("Error fetching prospects:", error.message);
        setError(error.message);
      } else {
        setProspects(data);
        localStorage.setItem("prospects", JSON.stringify(data));
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred.");
    }
  };

  const sortProspects = (field: string, direction: string) => {
    const sorted = [...prospects].sort((a, b) => {
      const aValue = field === "name"
        ? (isHE ? a.full_name[1] : a.full_name[0])
        : a.last_update ? new Date(a.last_update).getTime() : 0;

      const bValue = field === "name"
        ? (isHE ? b.full_name[1] : b.full_name[0])
        : b.last_update ? new Date(b.last_update).getTime() : 0;

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setProspects(sorted);
    setSortOrder({ field, direction });
  };

  useEffect(() => {
    if (didUpdate || prospects.length === 0) {
      fetchProspects();
    } else {
      const storedData = localStorage.getItem("prospects");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const sortedData = [...parsedData].sort((a, b) => {
          const aDate = a.last_update ? new Date(a.last_update).getTime() : null;
          const bDate = b.last_update ? new Date(b.last_update).getTime() : null;

          if (aDate === null && bDate === null) return 0;
          if (aDate === null) return 1;
          if (bDate === null) return -1;
          return bDate - aDate;
        });
        setProspects(sortedData);
      }
    }
  }, [didUpdate]);

  const navigateToProspect = (prospect: Prospect) => {
    if (prospect.last_update === null) {
      return;
    } else {
      navigate(`/prospects/${prospect.id}/${prospect.full_name[0].replace(/\s+/g, '-')}`);
    }
  };

  return (
    <div className="min-h-screen bg-terminal flex flex-col items-center justify-n p-2">
      {error ? (
        <p className="text-red-500 text-lg">Error loading prospects: {error}</p>
      ) : (
        <>
          <div
            className={
              isHE ?
                "flex flex-row-reverse gap-4 mb-2" :
                "flex gap-4 mb-2"
            }
          >
            <button
              onClick={() => sortProspects("name", sortOrder.direction === "asc" ? "desc" : "asc")}
              className="w-15 h-15 px-2 py-1 bg-slate-100 text-gray-700 md:px-4 md:py-2 rounded shadow hover:bg-slate-200 flex items-center gap-2"
            >
              {
                isHE ?
                  'מיון לפי שם פרטי' :
                  'Sort by First Name'
              }
              {sortOrder.field === "name" &&
                (sortOrder.direction === "asc" ? <FcGenericSortingAsc /> : <FcGenericSortingDesc />)}
            </button>
            <button
              onClick={() =>
                sortProspects("last_update", sortOrder.direction === "asc" ? "desc" : "asc")
              }
              className="w-15 h-15 px-2 py-1 bg-slate-100 text-gray-700 md:px-4 md:py-2 rounded shadow hover:bg-slate-200 flex items-center gap-2"
            >
              {
                isHE ?
                  'מיון לפי מועד עדכון' :
                  'Sort by Last Update'
              }
              {sortOrder.field === "last_update" &&
                (sortOrder.direction === "asc" ? <FcGenericSortingAsc /> : <FcGenericSortingDesc />)}
            </button>
          </div>
          {prospects.length > 0 ? (
            <ul className="w-full max-w-2xl  rounded-lg shadow-md"
              dir={isHE ? 'rtl' : 'ltr'}
            >
              {prospects.map((prospect) => (
                <li
                  key={prospect.id}
                  className=" px-4 py-2 font-bold rounded last:border-none flex justify-between items-center text-orange-400 text-lg hover:bg-orange-400 hover:text-white cursor-pointer"
                  onClick={() => navigateToProspect(prospect)}
                >
                  <span
                  className="text-left"
                  >{isHE ? prospect.full_name[1] : prospect.full_name[0]}</span>
                  <span className="text-sm">
                    {isHE ? "עדכון אחרון" : "Last Update"}: {formatDate(prospect.last_update, isHE)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-700 text-lg">No prospects found.</p>
          )}
        </>
      )}
    </div>
  );
};

export default ProspectsTerminalPage;
